import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { PaziaModal } from "components/molecules";
import { PaziaButton, PaziaCheckbox, ButtonSwitch } from "components/atoms";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { ScheduleEventApi } from "api/scheduleEvents/scheduleEventsApi";
import { TopicApi } from "api/topics/topicsApi";
import { fetchTopicGroupDetails } from "api/hooks";
import {
  SCHEDULE_EXPORT_COLUMNS,
  ScheduleExportColumnKey,
  mapEventsToExportTable,
  exportScheduleCSV,
  exportSchedulePDF,
} from "./scheduleExportUtils";

interface ExportScheduleModalProps {
  isOpen: boolean;
  onCloseRequest: () => void;
  events: ScheduleEventApi[];
  courseId: number;
  /** Printed in the PDF header so an exported file says which edition it is. */
  courseName?: string;
  // Ids of rows ticked in the schedule list; when non-empty only these events are exported.
  selectedScheduleIds?: number[];
}

interface ExportFormValues {
  includeTopics: boolean;
  includeBreaks: boolean;
  includeExams: boolean;
  includeTopicGroups: boolean;
  columns: Record<ScheduleExportColumnKey, boolean>;
}

// Per-browser convenience: remember the last column selection.
const COLUMNS_STORAGE_KEY = "scheduleExportColumns";

const getDefaultColumns = (): Record<ScheduleExportColumnKey, boolean> => {
  const defaults = Object.fromEntries(
    SCHEDULE_EXPORT_COLUMNS.map((column) => [column.key, column.key !== "groupTopics"]),
  ) as Record<ScheduleExportColumnKey, boolean>;
  try {
    const stored = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY) ?? "null");
    if (stored && typeof stored === "object") {
      for (const column of SCHEDULE_EXPORT_COLUMNS) {
        if (typeof stored[column.key] === "boolean") defaults[column.key] = stored[column.key];
      }
    }
  } catch {
    // ignore - fall back to defaults
  }
  return defaults;
};

const getDefaultValues = (): ExportFormValues => ({
  includeTopics: true,
  includeBreaks: true,
  includeExams: true,
  includeTopicGroups: true,
  columns: getDefaultColumns(),
});

export const ExportScheduleModal = ({
  isOpen,
  onCloseRequest,
  events,
  courseId,
  courseName,
  selectedScheduleIds = [],
}: ExportScheduleModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [format, setFormat] = useState<string>("CSV");
  const queryClient = useQueryClient();

  const methods = useForm<ExportFormValues>({ defaultValues: getDefaultValues() });

  const { handleSubmit, watch, reset } = methods;
  const includeTopicGroups = watch("includeTopicGroups");
  const columnsValue = watch("columns");
  const selectedColumns = SCHEDULE_EXPORT_COLUMNS.filter((column) => columnsValue?.[column.key]).map(
    (column) => column.key,
  );

  const exportsSelectionOnly = selectedScheduleIds.length > 0;
  const eventsToExport = exportsSelectionOnly
    ? events.filter((event) => selectedScheduleIds.includes(event.id))
    : events;

  const onSubmit = async (data: ExportFormValues) => {
    try {
      setIsLoading(true);

      try {
        localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(data.columns));
      } catch {
        // ignore - remembering the selection is optional
      }

      let topicGroupDetailsMap: Map<number, TopicApi[]> | undefined;

      if (data.includeTopicGroups && selectedColumns.includes("groupTopics")) {
        const uniqueGroupIds = [
          ...new Set(
            eventsToExport
              .filter((e) => e.topicGroupId != null)
              .map((e) => e.topicGroupId!),
          ),
        ];

        if (uniqueGroupIds.length > 0) {
          const groupDetails = await Promise.all(
            uniqueGroupIds.map((id) => fetchTopicGroupDetails(queryClient, id)),
          );

          topicGroupDetailsMap = new Map();
          groupDetails.forEach((group) =>
            topicGroupDetailsMap!.set(group.id, group.topics),
          );
        }
      }

      const table = mapEventsToExportTable(
        eventsToExport,
        { ...data, columns: selectedColumns },
        topicGroupDetailsMap,
      );

      const fileName = `harmonogram_kursu_${courseId}`;

      if (format === "CSV") {
        exportScheduleCSV(table, fileName);
      } else {
        exportSchedulePDF(table, fileName, courseName);
      }

      handleClose();
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset(getDefaultValues());
    setFormat("CSV");
    onCloseRequest();
  };

  return (
    <PaziaModal
      id="export-schedule-modal"
      isOpen={isOpen}
      onCloseRequest={handleClose}
      size="lg"
    >
      <FormProvider {...methods}>
        <ModalPadding form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Eksportuj harmonogram</ModalHeader>

          {exportsSelectionOnly && (
            <p className="mb-4 rounded bg-primary-50 p-3 text-sm text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              Zostaną wyeksportowane tylko wybrane wydarzenia ({eventsToExport.length}). Aby wyeksportować cały
              harmonogram, odznacz wszystkie wiersze na liście.
            </p>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Format eksportu
            </label>
            <ButtonSwitch
              a="CSV"
              b="PDF"
              value={format}
              onChange={(val) => setFormat(val)}
            />
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Elementy do eksportu
            </label>
            <div className="flex flex-col gap-3">
              <PaziaCheckbox name="includeTopics" label="Tematy" />
              <PaziaCheckbox name="includeBreaks" label="Przerwy" />
              <PaziaCheckbox name="includeExams" label="Egzaminy" />
              <PaziaCheckbox name="includeTopicGroups" label="Grupy tematów" />
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Kolumny
            </label>
            <div className="grid grid-cols-2 gap-3">
              {SCHEDULE_EXPORT_COLUMNS.map((column) => (
                <PaziaCheckbox
                  key={column.key}
                  name={`columns.${column.key}`}
                  label={column.label}
                  disabled={column.key === "groupTopics" && !includeTopicGroups}
                />
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              „Tematy w grupie” wypisuje tematy w jednej komórce wiersza grupy - bez dodatkowych wierszy.
            </p>
            {selectedColumns.length === 0 && (
              <p className="mt-2 text-xs text-red-500">Wybierz co najmniej jedną kolumnę.</p>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <PaziaButton
              type="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Anuluj
            </PaziaButton>
            <PaziaButton
              type="primary"
              btnType="submit"
              disabled={isLoading || selectedColumns.length === 0}
              processing={isLoading}
            >
              Eksportuj
            </PaziaButton>
          </div>
        </ModalPadding>
      </FormProvider>
    </PaziaModal>
  );
};
